import { FC, useCallback, useMemo } from 'react';
import { Controller, useFormContext, useFieldArray } from 'react-hook-form';
import { isArray } from 'lodash';
import styled from 'styled-components';
import Editor from '@monaco-editor/react';
import { useDeepCompareMemo } from '@clatter/platform';

import { CircularLoader, EditorControl } from '@clatter/ui';
import { useGetFolderAssetsQuery } from '../../../services';
import { ImagePicker } from './ImagePicker';
import { OrderedList } from './OrderedList';
import { FormInputWrapper } from './FormInputWrapper';
import { DropdownSelect } from '../DropdownSelect/DropdownSelect';

interface DynamicFormFieldProps {
  register?: any;
  control?: any;
  disabled?: boolean;
  required?: boolean;
  data: DynamicFormField;
  prefix?: string;
  queryGqlData?: (query: string) => Promise<Record<string, any>>;
}

interface ImagePickerInputProps {
  data: ImagePickerDynamicFormField | VideoPickerDynamicFormField;
  prefix?: string;
}

const StyledImagePickerContainer = styled.div`
  display: grid;
  overflow: hidden;
  border: ${({ theme }) => `1px solid  ${theme.palette.stroke};`};
  padding: ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme?.radius?.small};
`;

const StyledCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 16px;
  height: 16px;
  margin: 0;
  flex-shrink: 0;
  accent-color: #1976d2;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const StyledCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};

  input[type='checkbox'] {
    accent-color: #1976d2;
    width: 16px;
    height: 16px;
    cursor: pointer;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    &:hover input[type='checkbox']:not(:disabled) {
      accent-color: #1565c0;
    }
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 10px;
  font-size: 16px;
  border: 2px solid grey;
  border-radius: 5px;
  resize: vertical;
`;

interface PagesPickerInputProps {
  data: PagesPickerDynamicFormField;
  prefix?: string;
}

export const PagesPickerInput: FC<PagesPickerInputProps> = ({ data, prefix = '' }) => {
  const { control } = useFormContext();
  const { name, inputOptions } = data || {};

  // Parse folderId from "sourcePath"
  const folderId = (inputOptions?.sourcePath || '').split(':').pop();

  const { data: assetsFromApi, isLoading } = useGetFolderAssetsQuery({ parentId: +folderId! }, { skip: !folderId });

  if (!folderId) {
    console.error('Error: no source folder Id provided');
  }

  const variantSuffix = inputOptions?.settings?.variantSuffix;

  // Build the orderIndex map
  const orderIndex = useMemo(() => {
    const order = inputOptions?.settings?.order || [];
    const map = new Map<string, number>();
    order.forEach((n, i) => map.set(n, i));
    return map;
  }, [inputOptions]);

  // Sorting helper
  const sortBySchemaOrder = useCallback(
    <T extends { name?: string }>(arr: T[]) => {
      return [...arr]
        .map((item, originalIdx) => {
          const idx =
            item?.name != null && orderIndex.has(item.name)
              ? (orderIndex.get(item.name) as number)
              : Number.POSITIVE_INFINITY; // unknown names at the end
          return { item, idx, originalIdx };
        })
        .sort((a, b) => {
          if (a.idx !== b.idx) return a.idx - b.idx;
          return a.originalIdx - b.originalIdx;
        })
        .map(({ item }) => item);
    },
    [orderIndex],
  );

  // Prepare assetsData (with optional variant handling)
  const assetsData = useDeepCompareMemo(() => {
    if (!assetsFromApi) return [];

    const mappedAssets = assetsFromApi.map((item) => ({
      id: item.currentVersion?.AssetID,
      name: item.name,
    }));

    if (variantSuffix) {
      // split into base and variant
      const baseItems = mappedAssets.filter((item) => !item?.name?.includes(variantSuffix));
      const variantItems = mappedAssets.filter((item) => item?.name?.includes(variantSuffix));

      const withVariants = baseItems.map((base) => {
        const baseName = base.name?.replace(/\.[^.]+$/, ''); // strip extension

        const variant = variantItems.find((variant) => {
          const variantName = variant.name?.replace(/\.[^.]+$/, '');
          return variantName === `${baseName}_${variantSuffix}`;
        });

        return {
          ...base,
          variant,
        };
      });

      return sortBySchemaOrder(withVariants);
    }

    return sortBySchemaOrder(mappedAssets);
  }, [assetsFromApi, variantSuffix, inputOptions, sortBySchemaOrder]);

  // Required assets
  const requiredData = useDeepCompareMemo(() => {
    const requiredNames = inputOptions?.settings?.required || [];
    const filtered = assetsData.filter((item: any) => requiredNames.includes(item.name));

    return {
      assets: sortBySchemaOrder(filtered),
      ids: filtered.map((item: any) => item.id),
    };
  }, [assetsData, inputOptions, sortBySchemaOrder]);

  if (isLoading) {
    return <CircularLoader />;
  }

  return (
    <StyledImagePickerContainer>
      <Controller
        name={`${prefix}${name}`}
        control={control}
        render={({ field: { onChange, value = [] } }) => {
          // if empty, pre-fill with required assets
          const initialValue = value.length === 0 ? requiredData.assets : sortBySchemaOrder(value);

          return (
            <ImagePicker
              images={assetsData}
              urlProperty="thumbnailUrl"
              value={initialValue}
              onChange={(clickedArray) => {
                if (isArray(clickedArray)) {
                  const sorted = sortBySchemaOrder(clickedArray);
                  onChange(sorted);
                }
              }}
              fetchPresignedThumbnail
              required={data.required}
              variant={data.inputOptions.pickerVariant}
              requiredIds={requiredData?.ids}
              multiple={true}
              maxImageHeight={180}
            />
          );
        }}
      />
    </StyledImagePickerContainer>
  );
};

const ImagePickerInput: FC<ImagePickerInputProps> = ({ data, prefix = '' }) => {
  const { control } = useFormContext();

  const { name, inputOptions } = data || {};

  //crn:folders:50
  const folderId = (inputOptions?.sourcePath || '').split(':').pop();

  const { data: assetsData, isLoading } = useGetFolderAssetsQuery({ parentId: +folderId! }, { skip: !folderId });

  if (!folderId) {
    console.error('Error: no source folder Id provided');
  }

  if (isLoading) {
    return <CircularLoader />;
  }

  return (
    <StyledImagePickerContainer>
      <Controller
        name={`${prefix}${name}`}
        control={control}
        render={({ field: { onChange, value } }) => (
          <ImagePicker
            images={assetsData || []}
            urlProperty="thumbnailUrl"
            value={{ id: value }}
            onChange={(data) => onChange(data?.id ?? '')}
            fetchPresignedThumbnail
            required={data.required}
            variant={data.inputOptions.pickerVariant}
          />
        )}
      />
    </StyledImagePickerContainer>
  );
};

const OrderedListInput = ({
  prefix = '',
  data,
  queryGqlData,
}: {
  prefix: string;
  data: OrderedListDynamicFormField;
  queryGqlData?: (query: string) => Promise<Record<string, any>>;
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const { fields, append, prepend, remove, swap, move, insert } = useFieldArray({
    control,
    name: `${prefix}${data.name}`,
  });

  return (
    <OrderedList
      fields={fields}
      data={data}
      prefix={prefix}
      InputRenderer={DynamicFormField}
      queryGqlData={queryGqlData}
      append={append}
      remove={remove}
      swap={swap}
    />
  );
};

const StyledRichTextWrapper = styled.div`
  .rdw-link-modal {
    background-color: ${({ theme }) => theme.palette.white};

    color: ${({ theme }) => theme.palette.text};

    input {
      background-color: ${({ theme }) => theme.palette.white};
    }

    button {
      background-color: ${({ theme }) => theme.palette.white};
      color: ${({ theme }) => theme.palette.text};

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        color: ${({ theme }) => theme.palette.textLight || '#999'};
      }
    }

    input[type='checkbox'] {
      width: 15px;
      height: 15px;
      margin-right: 5px;
      vertical-align: middle;
    }

    label {
      display: flex;
      align-items: center;
      font-size: 13px;
      margin: 8px 0 0 0;
    }

    input[type='text'] {
      width: 100%;
      padding: 5px;
      margin: 5px 0;
      border: 1px solid #ddd;
      border-radius: 2px;
    }
  }

  .editor-control-body {
    margin-top: 1rem;
    background-color: ${({ theme }) => theme.palette.white};
  }

  a {
    color: blue;
    text-decoration: underline;
  }

  .editor-control-body {
    font-size: 1rem;
    line-height: 1.5;
    margin-top: 0.5rem;
    background-color: ${({ theme }) => theme.palette.white};
  }
`;

export const DynamicFormField: FC<DynamicFormFieldProps> = ({ data, prefix = '', disabled = false, queryGqlData }) => {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();

  const formField = data || {};
  const inputName = `${prefix}${formField.name}`;

  switch (formField.inputType) {
    case 'email':
      return <input type="email" disabled={disabled} {...register(inputName)} />;
    case 'text':
    case 'hidden':
      return <input type={formField.inputType} {...register(inputName)} disabled={disabled} />;
    case 'textarea':
      return <StyledTextarea rows={formField.inputOptions.rows || 5} disabled={disabled} {...register(inputName)} />;
    // NOTE: EditorControl will not work properly with react
    // strict mode enabled! Popup for links will not show up.
    case 'rich-text':
      return (
        <Controller
          name={inputName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <StyledRichTextWrapper>
              <EditorControl
                wysiwygOptions={['inline', 'link', 'list']}
                onChange={(val: any) => {
                  const clean = val.trim();
                  if (clean === '<p></p>' || clean === '<p><br></p>') {
                    onChange('');
                  } else {
                    onChange(val);
                  }
                }}
                value={value || ''}
                readOnly={disabled}
              />
            </StyledRichTextWrapper>
          )}
        />
      );
    case 'editor':
      return (
        <Controller
          name={inputName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Editor
              height={formField?.inputOptions?.height || '100%'}
              width="100%"
              language={formField?.inputOptions?.language || 'html'}
              value={value}
              onChange={onChange}
              loading={disabled}
              options={{
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontSize: 14,
                lineNumbers: 'on',
                roundedSelection: false,
                scrollbar: {
                  vertical: 'auto',
                  horizontal: 'auto',
                },
                automaticLayout: true,
                wordWrap: 'on',
                tabSize: 2,
                insertSpaces: true,
              }}
              theme="vs-dark"
            />
          )}
        />
      );
    //@ts-ignore
    case 'pages-picker':
      return <PagesPickerInput data={formField} prefix={prefix} />;
    case 'video-picker':
      return <ImagePickerInput data={formField} prefix={prefix} />;
    case 'image-picker':
      return <ImagePickerInput data={formField} prefix={prefix} />;
    case 'queryKey':
      return null;
    case 'ordered-list': {
      return <OrderedListInput data={formField} prefix={prefix} queryGqlData={queryGqlData} />;
    }
    case 'object': {
      return (
        <div>
          {(formField.items || []).map((field: any, index: number) => (
            <div key={index}>
              <FormInputWrapper
                key={field.name}
                label={field.label}
                required={field.required}
                errorMessage={(errors?.[inputName + '.' + field.name]?.message as string) || null}
                hidden={field.inputType === 'hidden'}
              >
                <DynamicFormField
                  data={field}
                  prefix={inputName + '.'}
                  disabled={disabled}
                  queryGqlData={queryGqlData}
                />
              </FormInputWrapper>
            </div>
          ))}
        </div>
      );
    }
    case 'select': {
      return (
        <Controller
          name={inputName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <DropdownSelect
              placeholder="Select..."
              value={value}
              options={formField.inputOptions.selectOptions || []}
              isDisabled={disabled || formField?.inputOptions?.disabled}
              onChange={onChange}
            />
          )}
        />
      );
    }
    case 'checkbox':
      return (
        <Controller
          name={inputName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <StyledCheckboxLabel style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <StyledCheckbox
                type="checkbox"
                disabled={disabled}
                checked={!!value}
                onChange={(e) => onChange(e.target.checked, { shouldDirty: true })}
                style={{ width: '16px', height: '16px', margin: 0, flexShrink: 0 }}
              />
              <span>{formField.label}</span>
            </StyledCheckboxLabel>
          )}
        />
      );

    case 'checkbox-group':
      return (
        <StyledCheckboxGroup className="checkbox-list">
          {(formField.inputOptions.checkboxOptions || []).map((option: any, index: number) => {
            return (
              <StyledCheckboxLabel key={index}>
                <StyledCheckbox type="checkbox" disabled={disabled} value={option.value} {...register(inputName)} />
                {option?.icon}
                {option.label}
              </StyledCheckboxLabel>
            );
          })}
        </StyledCheckboxGroup>
      );
    default:
      if (formField.inputType === undefined) {
        return <input type="text" {...register(inputName)} disabled={disabled} />;
      } else {
        return <div>Unknown field type!</div>;
      }
  }
};
